import { initializeApp, getApps, getApp } from "firebase/app";
import {
  getAuth, GoogleAuthProvider, signInWithPopup,
  RecaptchaVerifier, signInWithPhoneNumber,
  sendSignInLinkToEmail, isSignInWithEmailLink, signInWithEmailLink,
  setPersistence, browserLocalPersistence,
} from "firebase/auth";

const cfg = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

export const firebaseReady = !!cfg.apiKey;
const app = firebaseReady ? (getApps().length ? getApp() : initializeApp(cfg)) : null;
export const auth = app ? getAuth(app) : null;
if (auth) { try { setPersistence(auth, browserLocalPersistence); } catch (e) { /* noop */ } }

const EMAIL_KEY = "ap_email_for_signin";
const PENDING_URL_KEY = "ap_pending_email_link";

// ---- Google ----
export async function googleSignIn() {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  const res = await signInWithPopup(auth, provider);
  return res.user.getIdToken();
}

// ---- Phone (SMS OTP) ----
let recaptcha = null;
export function setupRecaptcha(containerId = "recaptcha-container") {
  if (recaptcha) return recaptcha;
  recaptcha = new RecaptchaVerifier(auth, containerId, { size: "invisible" });
  return recaptcha;
}
export function resetRecaptcha() {
  try { if (recaptcha) recaptcha.clear(); } catch (e) { /* noop */ }
  recaptcha = null;
}
export async function sendPhoneOtp(e164Phone) {
  const verifier = setupRecaptcha();
  return signInWithPhoneNumber(auth, e164Phone, verifier);
}
export async function confirmPhoneOtp(confirmationResult, code) {
  const res = await confirmationResult.confirm(code);
  return res.user.getIdToken();
}

// ---- Email magic link (with safe-click pattern) ----
export async function sendEmailLink(email) {
  const acs = { url: `${window.location.origin}/login`, handleCodeInApp: true };
  await sendSignInLinkToEmail(auth, email, acs);
  window.localStorage.setItem(EMAIL_KEY, email);
}
export function isEmailLink(url = window.location.href) {
  return auth ? isSignInWithEmailLink(auth, url) : false;
}
export function savedEmailForLink() { return window.localStorage.getItem(EMAIL_KEY) || ""; }
// IMPORTANT: do NOT auto-consume the OOB code on load — Gmail's URL scanner will burn it.
// Stash the URL and let the user click a confirm button.
export function stashPendingEmailLink(url = window.location.href) {
  window.localStorage.setItem(PENDING_URL_KEY, url);
}
export function getPendingEmailLink() { return window.localStorage.getItem(PENDING_URL_KEY) || ""; }
export function clearPendingEmailLink() { window.localStorage.removeItem(PENDING_URL_KEY); }
export async function completeEmailLink(email, url) {
  const link = url || getPendingEmailLink() || window.location.href;
  const res = await signInWithEmailLink(auth, email, link);
  window.localStorage.removeItem(EMAIL_KEY);
  clearPendingEmailLink();
  return res.user.getIdToken();
}

export function humanizeFirebaseError(err) {
  const code = err?.code || "";
  const map = {
    "auth/popup-closed-by-user": "Sign-in was cancelled.",
    "auth/cancelled-popup-request": "Sign-in was cancelled.",
    "auth/popup-blocked": "Your browser blocked the popup. Please allow popups and try again.",
    "auth/account-exists-with-different-credential": "An account already exists with a different sign-in method.",
    "auth/invalid-phone-number": "That phone number doesn't look right — include your country code.",
    "auth/missing-phone-number": "Please enter your phone number.",
    "auth/too-many-requests": "Too many attempts. Please wait a little and try again.",
    "auth/invalid-verification-code": "That code is incorrect. Please check and retry.",
    "auth/code-expired": "That code expired. Please request a new one.",
    "auth/invalid-email": "Please enter a valid email address.",
    "auth/network-request-failed": "Network error. Check your connection and retry.",
    "auth/unauthorized-domain": "This site isn't authorized for sign-in yet. Please try the email link.",
    "auth/operation-not-allowed": "This sign-in method isn't enabled yet.",
  };
  return map[code] || (err?.message ? err.message.replace("Firebase: ", "") : "Something went wrong. Please try again.");
}
